import { Head, Link } from "@inertiajs/react"
import { PublicShell } from "@/components/PublicShell"
import { Trophy } from "lucide-react"

type Leader = {
  name:           string
  total_answered: number
  total_correct:  number
  percentage:     number
}

type Props = { leaders: Leader[] }

const title       = "Trivia Leaderboard — Australian Music Database"
const description = "The top scorers in the Australian Music Database trivia quiz."

export default function Leaderboard({ leaders }: Props) {
  return (
    <>
      <Head title={title}>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
      </Head>
      <PublicShell>
        <div className="max-w-2xl mx-auto py-10 px-4">
          <div className="flex items-center gap-3 mb-8">
            <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-amber-50 dark:bg-amber-950/30">
              <Trophy className="w-5 h-5 text-amber-500" />
            </div>
            <div>
              <h1 className="mb-0">Trivia Leaderboard</h1>
              <p className="text-sm text-ink-muted">Ranked by percentage of correct answers (minimum 10 answered)</p>
            </div>
          </div>

          {leaders.length === 0 ? (
            <div className="rounded-xl border border-hairline bg-surface p-10 text-center">
              <p className="text-ink-muted mb-4">No one on the leaderboard yet — be the first!</p>
              <Link href="/trivia" className="text-amber-600 dark:text-amber-400 hover:underline text-sm">Play the quiz →</Link>
            </div>
          ) : (
            <div className="rounded-xl border border-hairline bg-surface overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-hairline bg-stone-50 dark:bg-stone-900">
                    <th className="text-left px-2 sm:px-4 py-3 font-medium text-ink-muted w-8">#</th>
                    <th className="text-left px-2 sm:px-4 py-3 font-medium text-ink-muted">Name</th>
                    <th className="hidden sm:table-cell text-right px-4 py-3 font-medium text-ink-muted">Answered</th>
                    <th className="text-right px-2 sm:px-4 py-3 font-medium text-ink-muted">Correct</th>
                    <th className="text-right px-2 sm:px-4 py-3 font-medium text-ink-muted">Score</th>
                  </tr>
                </thead>
                <tbody>
                  {leaders.map((l, i) => (
                    <tr key={i} className={`border-b border-hairline last:border-0 ${i === 0 ? "bg-amber-50/50 dark:bg-amber-950/10" : ""}`}>
                      <td className="px-2 sm:px-4 py-3 text-ink-muted tabular-nums">
                        {i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : i + 1}
                      </td>
                      <td className="px-2 sm:px-4 py-3 font-medium text-ink-display max-w-[9rem] sm:max-w-none truncate">{l.name}</td>
                      <td className="hidden sm:table-cell text-right px-4 py-3 text-ink-muted tabular-nums">{l.total_answered.toLocaleString()}</td>
                      <td className="text-right px-2 sm:px-4 py-3 text-ink-muted tabular-nums">{l.total_correct.toLocaleString()}</td>
                      <td className="text-right px-2 sm:px-4 py-3 font-medium text-ink-display tabular-nums">{l.percentage}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <p className="text-xs text-ink-muted mt-6 text-center">
            Only players who opt in to leaderboard visibility appear here.{" "}
            <Link href="/profile/details" className="underline">Update your preferences</Link>.
          </p>
        </div>
      </PublicShell>
    </>
  )
}
